import type { BaseServiceParams } from '@kiddocare-platform/portal-common/base';
import { useAppContext } from '@kiddocare-platform/portal-common/contexts';
import type { AppContextType } from '@kiddocare-platform/portal-common/contexts';
import type { Setting } from '@kiddocare-platform/portal-common/core';
import { useLogger } from '@kiddocare-platform/portal-common/helpers';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { PlatformAtlasService } from '../modules/platform/services/atlas.service';
import { PlatformIntegrationService } from '../modules/platform/services/integration.service';
import { PlatformLegalService } from '../modules/platform/services/legal.service';
import { PlatformOrbitService } from '../modules/platform/services/orbit.service';
import { PlatformRegistryService } from '../modules/platform/services/registry.service';
import { RepositoryAvatarsService } from '../modules/repository/services/avatars.service';

export interface AdminContextType extends AppContextType {
  isSettingsLoaded: boolean;
  settings: Array<Setting> | null;
  //coreRegistryService: CoreRegistryService,
  repositoryAvatarsService: RepositoryAvatarsService,
  platformIntegrationService: PlatformIntegrationService,
  platformLegalService: PlatformLegalService,
  platformOrbitService: PlatformOrbitService,
  platformRegistryService: PlatformRegistryService,
  platformAtlasService: PlatformAtlasService
}

// Crea il contesto con il tipo e un valore iniziale
export const AdminContext = createContext<AdminContextType | undefined>(undefined);

// Hook per accedere al contesto in modo più semplice
export const useAdminContext = () => {
  const context = useContext(AdminContext);
  if (context === undefined) { throw new Error("AdminContext not found"); }
  return context;
};

export const AdminContextProvider: React.FC<{ children: ReactNode }> = (props) => {
  const { children } = props;
  const log = useLogger(AdminContextProvider.name);
  const { accessToken, user, handleLastActivity, ...restAppContext } = useAppContext();

  const [settings, setSettings] = useState<Array<Setting> | null>(null);
  const [isSettingsLoaded, setIsSettingsLoaded] = useState<boolean>(false);

  const services = useMemo(() => {
    if (!accessToken) {
      log.debug("$$$ : Access token non disponibile, servizi non inizializzati");
      return {} as AdminContextType;
    }

    log.debug("$$$ Inizializzo i servizi");
    //const coreProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_CORE_URL, onRequest: handleLastActivity };
    const repositoryProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_REPOSITORY_URL, onRequest: handleLastActivity };
    const platformProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_PLATFORM_URL, onRequest: handleLastActivity };

    return {
      //coreRegistryService: new CoreRegistryService(coreProps),
      repositoryAvatarsService: new RepositoryAvatarsService(repositoryProps),

      platformIntegrationService: new PlatformIntegrationService(platformProps),
      platformLegalService: new PlatformLegalService(platformProps),
      platformOrbitService: new PlatformOrbitService(platformProps),
      platformRegistryService: new PlatformRegistryService(platformProps),
      platformAtlasService: new PlatformAtlasService(platformProps),
    }
  }, [accessToken]);

  const fetchSettings = useCallback(async () => {
    try {
      log.info("[#########################################] FETCH SETTINGS");
      //const response = await services.coreRegistryService.CheckAccount();
      //log.info("###Fetch Settings", response.data.length);
      //const settingsSort = response.data?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      //setSettings(settingsSort);
      setSettings(null);
    } catch (error: any) {
      log.error("###Errore Fetch Settings", error);
    } finally {
      setIsSettingsLoaded(true);
    }
  }, [accessToken]);

  useEffect(() => {
    log.debug("[useEffect] ", user);
    if (user && !isSettingsLoaded) {
      log.info("[useEffect] user autenticato, recupero settings");
      fetchSettings();
    }
    /*if (user && !isParentLoaded) {
      log.info("[useEffect] user autenticato, recupero pediatrician");
      fetchParent();
    }
    if (user && !isPatientsLoaded) {
      log.info("[useEffect] user autenticato, recupero pazienti");
      fetchPatients();
    }*/
  }, [user]);

  const value: AdminContextType = {
    accessToken,
    user,
    handleLastActivity,
    isSettingsLoaded,
    settings,
    ...restAppContext,
    ...services
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}